/**
 * Simulate Screen
 *
 * Standalone entry point for the pre-trade impact simulation (§8.1).
 * Collects symbol / action / amount and shows the result via SimulatePanel.
 * THEME-AWARE and i18n SUPPORTED
 */

import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useUserSettings } from '../../services/api/queries/useUserSettings';
import { SimulatePanel } from '../../components/portfolio/SimulatePanel';

export function SimulateScreen() {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  // The backend weighs the amount against base-currency position values —
  // accept and label the amount in the user's actual base currency, not USD.
  const { data: settings } = useUserSettings();
  const baseCurrency = settings?.base_currency ?? 'USD';

  const [symbol, setSymbol] = useState('');
  const [action, setAction] = useState<'buy' | 'sell'>('buy');
  const [amount, setAmount] = useState('');
  const [panelVisible, setPanelVisible] = useState(false);

  const amountUsd = parseFloat(amount);
  const canSimulate = symbol.trim().length > 0 && Number.isFinite(amountUsd) && amountUsd > 0;

  return (
    <ScrollView style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={[styles.formContainer, { backgroundColor: theme.colors.surface }]}>
        {/* Action Selection */}
        <View style={styles.fieldContainer}>
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {t('simulate.action', { defaultValue: 'Action' })} *
          </Text>
          <View style={styles.actionOptionsContainer}>
            {(['buy', 'sell'] as const).map((value) => {
              const isSelected = action === value;
              const backgroundColor = isSelected
                ? value === 'sell'
                  ? theme.colors.error
                  : theme.colors.secondary
                : theme.colors.backgroundElevated;
              const borderColor = isSelected
                ? value === 'sell'
                  ? theme.colors.error
                  : theme.colors.secondary
                : theme.colors.border;
              return (
                <TouchableOpacity
                  key={value}
                  style={[styles.actionOptionButton, { flex: 1, backgroundColor, borderColor }]}
                  onPress={() => setAction(value)}
                >
                  <Text
                    style={[
                      styles.actionOptionText,
                      { color: isSelected ? theme.colors.textInverse : theme.colors.text },
                    ]}
                  >
                    {t(`portfolio.${value}`, { defaultValue: value === 'buy' ? 'Buy' : 'Sell' })}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        </View>

        {/* Symbol Input */}
        <View style={styles.fieldContainer}>
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {t('portfolio.symbol', { defaultValue: 'Symbol' })} *
          </Text>
          <TextInput
            style={[
              styles.input,
              {
                backgroundColor: theme.colors.backgroundElevated,
                borderColor: theme.colors.border,
                color: theme.colors.text,
              },
            ]}
            value={symbol}
            onChangeText={(text) => setSymbol(text.toUpperCase())}
            placeholder={t('portfolio.symbolPlaceholder', { defaultValue: 'e.g., AAPL' })}
            placeholderTextColor={theme.colors.textTertiary}
            autoCapitalize="characters"
            autoCorrect={false}
          />
        </View>

        {/* Amount Input */}
        <View style={styles.fieldContainer}>
          <Text style={[styles.label, { color: theme.colors.text }]}>
            {t('simulate.amountLabel', {
              currency: baseCurrency,
              defaultValue: `Amount (${baseCurrency})`,
            })}{' '}
            *
          </Text>
          <TextInput
            style={[
              styles.input,
              {
                backgroundColor: theme.colors.backgroundElevated,
                borderColor: theme.colors.border,
                color: theme.colors.text,
              },
            ]}
            value={amount}
            onChangeText={setAmount}
            placeholder={t('simulate.amountPlaceholder', { defaultValue: 'e.g., 1000' })}
            placeholderTextColor={theme.colors.textTertiary}
            keyboardType="decimal-pad"
          />
        </View>

        {/* Simulate Button */}
        <TouchableOpacity
          testID="simulate-run"
          style={[
            styles.button,
            { backgroundColor: canSimulate ? theme.colors.primary : theme.colors.border },
          ]}
          onPress={() => setPanelVisible(true)}
          disabled={!canSimulate}
        >
          <Text
            style={[
              styles.buttonText,
              { color: canSimulate ? theme.colors.textInverse : theme.colors.textTertiary },
            ]}
          >
            {t('simulate.run')}
          </Text>
        </TouchableOpacity>

        <SimulatePanel
          visible={panelVisible}
          onClose={() => setPanelVisible(false)}
          action={action}
          symbol={symbol.trim().toUpperCase()}
          amountUsd={amountUsd}
        />
      </View>
    </ScrollView>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      flex: 1,
    },
    formContainer: {
      padding: theme.spacing.lg,
      minHeight: '100%',
    },
    fieldContainer: {
      marginBottom: theme.spacing.lg,
    },
    label: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      marginBottom: theme.spacing.sm,
    },
    actionOptionsContainer: {
      flexDirection: 'row',
      gap: theme.spacing.sm,
    },
    actionOptionButton: {
      paddingVertical: theme.spacing.md,
      borderRadius: theme.radii.md,
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      alignItems: 'center',
    },
    actionOptionText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    input: {
      borderWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderRadius: theme.radii.md,
      padding: theme.spacing.md,
      fontSize: theme.typography.fontSizeMd,
    },
    button: {
      paddingVertical: theme.spacing.md,
      borderRadius: theme.radii.md,
      alignItems: 'center',
      marginTop: theme.spacing.sm,
    },
    buttonText: {
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightBold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
  });
}
